/**
 * Table Component
 */

import React, { FC, useState, useEffect, ReactElement, useRef, RefObject } from 'react';
import { withTheme } from 'styled-components';
import { TableProps, HeadProps, GroupHeadProps, CellTypes, CellHighlights } from './table.types';
import TableHeader from './TableHeader/table-header';
import TableRow from './TableRow/table-row';
import TableFooter from './TableFooter/table-footer';
import { StyledTable, TableScroller, TableCellPaddingConfig, TableContainer } from './table.style';
import COLORS from '../../style/universal.color';
import Handler from '../../commonTypes/handler.types';

const {
  mlbGrey1,
  mlbGrey5,
  mlbWhite,
  mlbBlack
} = COLORS;

// A unique counter to give each table a unique index
let tableCounter: number = 0;

// Create the array of table rows to be rendered
const createTableRows: Function = (
  columnRowData: any[],
  tableUniqueIndex: number,
  props: TableProps,
  dark: boolean,
  highlightedCells: CellHighlights[],
  highlightColor: string,
  tableRowHandlers: Handler,
  selectedRowIndex: number,
  isLoading: boolean,
  replaceCustomCellWithSkeleton: boolean,
  underlinedRowIndex: number,
  underlinedColor: string
): ReactElement[] => {
  const {
    tbody,
    customCells,
    highlightHead,
    columnsState,
    columnSortIndex,
    highlightSortedColumn
  } = props;

  return tbody.map((row: any, i: number) => (
    <TableRow
      tableIndex={ tableUniqueIndex }
      data={ row }
      columnData={ columnRowData }
      rowIndex={ i }
      type={ CellTypes.BODY }
      key={ `row-${i}` }
      customCells={ customCells }
      dark={ dark }
      highlightedCells={ highlightedCells }
      highlightHead={ highlightHead }
      firstRow={ i === 0 }
      lastRow={ i === tbody.length - 1 }
      columnsState={ columnsState }
      columnSortIndex={ columnSortIndex }
      highlightSortedColumn={ highlightSortedColumn }
      highlightColor={ highlightColor }
      tableRowHandlers={ tableRowHandlers }
      selectedRowIndex={selectedRowIndex}
      underlinedRowIndex={underlinedRowIndex}
      isLoading={ isLoading }
      replaceCustomCellWithSkeleton={ replaceCustomCellWithSkeleton }
    />
  ));
};

const Table: FC<TableProps> = (props: TableProps) => {

  const {
    thead,
    tbody,
    tfoot,
    width = '100%',
    maxWidth,
    tableFixed = false,
    customCells,
    startRight = false,
    bodyTdPadding = '12px 6px',
    bodyThPadding = '12px',
    headThPadding = '6px',
    hideDividers = false,
    highlightedCells = [],
    highlightHead,
    headerHandlers,
    columnsState,
    columnSortIndex,
    highlightSortedColumn,
    hoverRowHighlight = false,
    cellHighlightColor,
    theme = {},
    className,
    tableRowHandlers,
    selectedRowIndex,
    isLoading = false,
    replaceCustomCellWithSkeleton = false,
    tableHandlers,
    dataAttr,
    underlinedRowIndex,
    underlinedRowColor
  } = props;

  const paddingConfig: TableCellPaddingConfig = {
    bodyTdPadding,
    bodyThPadding,
    headThPadding
  };

  // Discover if we're using the dark theme
  const { dark = false } = theme;

  const highlightColor: string = cellHighlightColor || (dark ? mlbGrey5 : mlbGrey1);
  const underlinedColor: string = underlinedRowColor || (dark ? mlbWhite : mlbBlack);

  // Create a reference to the div wrapper so we can set the scroll position
  const divRef: RefObject<HTMLDivElement> | null = useRef<HTMLDivElement>(null);
  
  // Set the unique table index for this instance
  const [ tableUniqueIndex ] = useState(tableCounter++);

  // If startRight prop is true, start the scroll to the right side
  useEffect(() => {
    if (startRight && !!divRef && !!divRef.current) {
      divRef.current.scrollLeft = divRef.current.scrollWidth;
    }
  });

  // Create a table footer if data is passed
  const tableFooter: ReactElement | null = tfoot ? (
    <TableFooter
      type={ CellTypes.FOOTER }
      rowIndex={ tbody.length }
      tableIndex={ tableUniqueIndex }
      columnData={ thead }
      data={ tfoot }
      dark={ dark }
      columnsState={ columnsState }
      columnSortIndex={ columnSortIndex }
      isLoading={ isLoading }
      replaceCustomCellWithSkeleton={ replaceCustomCellWithSkeleton }
    />
  ) : null;

  const lastColIndex: number = thead.length - 1;

  // Retrieving the first and last cols, whether they be stand-alone or the first in a group.
  // Because of the magic of TypeScript, we have to cast it first as a group to check
  // if it has a group-only property 'groupColumns'. We then retrieve the necessary obj.
  // Afterwards, we use it for checking pinning and fixed widths for the pinned 
  // absolute positioning and parent margin placement
  const firstCol: HeadProps = !(thead[0] as GroupHeadProps).groupColumns ?
    (thead[0] as HeadProps) :
    (thead[0] as GroupHeadProps).groupColumns[0];

  const lastCol: HeadProps = !(thead[lastColIndex] as GroupHeadProps).groupColumns ?
    (thead[lastColIndex] as HeadProps) :
    (thead[lastColIndex] as GroupHeadProps).groupColumns[(thead[lastColIndex] as GroupHeadProps).groupColumns.length - 1];

  return (
    <TableContainer
      containerWidth={ width }
      containerMaxWidth={ maxWidth }>
      <TableScroller
        divWidth={ '100%' }
        hideDividers={ hideDividers }
        ref={divRef}
        dark={dark}
        leftPadding={ firstCol.pinned && (firstCol.fixedWidth || '100px') || '0px' }
        rightPadding={ lastCol.pinned && (lastCol.fixedWidth || '100px') || '0px' }
        {...tableHandlers}
        className={className}
        {...dataAttr}
      >
        <StyledTable
          tableFixed={ tableFixed }
          dark={ dark }
          paddingConfig={ paddingConfig }
          hideDividers={ hideDividers }
          tfootExists={ !!tableFooter }
          hoverRowHighlight={ hoverRowHighlight }
          highlightColor={ highlightColor }
          underlinedColor={underlinedColor}
          className={ className }>
          <TableHeader
            tableIndex={ tableUniqueIndex }
            columnData={ thead }
            customCells={ customCells }
            dark={ dark }
            highlightHead={ highlightHead }
            headerHandlers={ headerHandlers }
            columnsState={ columnsState }
            columnSortIndex={ columnSortIndex }
            highlightSortedColumn={ highlightSortedColumn }
            highlightColor={ highlightColor }
            isLoading={ isLoading }
            replaceCustomCellWithSkeleton={ replaceCustomCellWithSkeleton }
          />
          
          <tbody>
            { createTableRows(thead, tableUniqueIndex, props, dark, highlightedCells, highlightColor, tableRowHandlers, selectedRowIndex, isLoading, replaceCustomCellWithSkeleton, underlinedRowIndex, underlinedColor) }
          </tbody>
          
          { tableFooter }
        </StyledTable>
      </TableScroller>
    </TableContainer>
  );
};

export default withTheme(Table);
